import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { register } from "../../services/Service";
import { API_URL } from "../../services/config";
import "./Signup.css";
import { Check, ArrowRight, Lock, Mail, Eye, EyeOff, User, Building, Github as SiGithub } from 'lucide-react';
import { SiGoogle } from "react-icons/si";
import { toast } from "react-toastify";

const Signup = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Create account");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signupData, setSignupData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    organizationName: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    sessionStorage.clear();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setSignupData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = "var(--signup-teal)";
  };

  const handleBlur = (e) => {
    if (e.target.value.trim() !== "") {
      e.target.style.borderColor = "var(--signup-teal)";
    } else {
      e.target.style.borderColor = "#E5E7EB";
    }
  };

  const getErrorMessage = (error) => {
    // RTK Query unwrapped errors have status and data directly on the error object
    const status = error.status || error.response?.status;
    const data = error.data || error.response?.data;
    const serverMessage = data?.message || data?.msg;
    const serverError = data?.error;

    if (!status && !error.response) {
      if (error.message === "Network Error") {
        return "Network error. Please check your internet connection and try again.";
      }
      if (error.code === "ECONNABORTED") {
        return "Request timeout. The server took too long to respond. Please try again.";
      }
      return error.message || "Network error. Please check your connection and try again.";
    }

    if (status === 400) {
      return serverMessage || serverError || "Invalid input. Please check your details and try again.";
    }

    if (status === 401) {
      return serverMessage || serverError || "Invalid email or password. Please try again.";
    }

    if (status === 409) {
      return serverMessage || serverError || "This email or username is already registered. Please sign in or use a different one.";
    }

    if (status === 500) {
      if (serverError?.includes("salt") || serverMessage?.includes("decrypt")) {
        return "Server configuration error. Please contact support or try again later.";
      }
      return "Server error. Please try again later or contact support.";
    }

    return serverMessage || serverError || "Operation failed. Please try again.";
  };

  const validateForm = () => {
    if (!signupData.username?.trim()) {
      toast.warning("Username is required");
      return false;
    }
    if (!signupData.firstName?.trim()) {
      toast.warning("First name is required");
      return false;
    }
    if (!signupData.lastName?.trim()) {
      toast.warning("Last name is required");
      return false;
    }
    if (!signupData.organizationName?.trim()) {
      toast.warning("Organization name is required");
      return false;
    }
    if (!signupData.email?.trim()) {
      toast.warning("Email is required");
      return false;
    }
    if (!signupData.password?.trim()) {
      toast.warning("Password is required");
      return false;
    }
    if (!signupData.confirmPassword?.trim()) {
      toast.warning("Confirm Password is required");
      return false;
    }
    if (signupData.password !== signupData.confirmPassword) {
      toast.warning("Passwords do not match");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signupData.email)) {
      toast.warning("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      setStatus("Creating account...");

      const { confirmPassword, ...submitData } = signupData;

      const response = await register(submitData);
      console.log("Signup successful:", response);
      toast.success("Account created successfully!");
      setStatus(
        <span>
          Account created! <Check size={16} />
        </span>
      );
      setTimeout(() => {
        navigate("/login");
        setIsLoading(false);
      }, 800);
    } catch (err) {
      setIsLoading(false);
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
      setStatus("Create account");
      console.error("Authentication Error:", err);
    }
  };

  return (
    <div className="signup-page-wrapper">
      <div className="signup-bg-animation">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
        <div className="bg-shape shape-4"></div>
      </div>

      <div className="particles">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="particle"></div>
        ))}
      </div>

      <div className="signup-container">
        <div className="left-panel">
          <div className="logo-container">
            <svg className="product-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <path d="M 20 35 L 45 35" stroke="#1E40AF" strokeWidth="4" strokeLinecap="round"></path>
              <path d="M 20 50 L 55 50" stroke="#14B8A6" strokeWidth="4" strokeLinecap="round"></path>
              <path d="M 20 65 L 65 65" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round"></path>
              <path d="M 45 35 L 55 40 L 45 45 Z" fill="#1E40AF"></path>
              <path d="M 55 50 L 65 55 L 55 60 Z" fill="#14B8A6"></path>
              <path d="M 65 65 L 75 70 L 65 75 Z" fill="#F59E0B"></path>
              <circle cx="80" cy="50" r="8" fill="#1E40AF"></circle>
            </svg>
            <span className="logo-text">SequFlow</span>
          </div>

          <div className="hero-content">
            <h1 className="hero-title">Streamline your workflow</h1>
            <p className="hero-subtitle">
              Join thousands of teams automating processes, tracking progress in real-time, and delivering exceptional results with SequFlow's intelligent platform.
            </p>
          </div>

          <div className="signup-features">
            <div className="feature">
              <div className="feature-icon"><Check size={14} /></div>
              <span>Free forever</span>
            </div>
            <div className="feature">
              <div className="feature-icon"><Check size={14} /></div>
              <span>No credit card</span>
            </div>
            <div className="feature">
              <div className="feature-icon"><Check size={14} /></div>
              <span>Cancel anytime</span>
            </div>
          </div>

          <div className="flow-visualization">
            <div className="flow-viz-title">Workflow Progress</div>
            <div className="flow-lines">
              <div className="flow-item">
                <div className="flow-bar"></div>
                <div className="flow-arrow"><ArrowRight size={14} /></div>
              </div>
              <div className="flow-item">
                <div className="flow-bar"></div>
                <div className="flow-arrow"><ArrowRight size={14} /></div>
              </div>
              <div className="flow-item">
                <div className="flow-bar"></div>
                <div className="flow-arrow"><Check size={14} /></div>
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="form-container">
            <div className="form-header">
              <h2 className="form-title">Create account</h2>
              <p className="form-subtitle">
                Already have an account? <Link to="/login" className="signup-link">Sign in</Link>.
              </p>
            </div>

            <div className="social-login">
              <button className="social-btn"><SiGoogle size={18} /> Google</button>
              <button className="social-btn"><SiGithub size={18} /> GitHub</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <div className="input-wrapper">
                  <User className="input-icon" size={18} />
                  <input
                    type="text"
                    id="username"
                    placeholder="Choose your username"
                    required
                    value={signupData.username}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First name</label>
                  <div className="input-wrapper">
                    <User className="input-icon" size={18} />
                    <input
                      type="text"
                      id="firstName"
                      placeholder="First name"
                      required
                      value={signupData.firstName}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last name</label>
                  <div className="input-wrapper">
                    <User className="input-icon" size={18} />
                    <input
                      type="text"
                      id="lastName"
                      placeholder="Last name"
                      required
                      value={signupData.lastName}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="organizationName">Organization name</label>
                <div className="input-wrapper">
                  <Building className="input-icon" size={18} />
                  <input
                    type="text"
                    id="organizationName"
                    placeholder="Your organization name"
                    required
                    value={signupData.organizationName}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <div className="input-wrapper">
                  <Mail className="input-icon" size={18} />
                  <input
                    type="email"
                    id="email"
                    placeholder="you@company.com"
                    required
                    value={signupData.email}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="password-header">
                  <label htmlFor="password">Password</label>
                </div>
                <div className="input-wrapper">
                  <Lock className="input-icon" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Enter your password"
                    required
                    value={signupData.password}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <div className="password-header">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                </div>
                <div className="input-wrapper">
                  <Lock className="input-icon" size={18} />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    placeholder="Confirm your password"
                    required
                    value={signupData.confirmPassword}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={isLoading}>
                {status}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;

